import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { Guards } from 'interfaces/guards'
import { concatCollections, getItemsFromCollection } from 'helpers/normalizeHelpers'
import BalanceAccountSettingsModule, {
  BalanceAccountSettingsDispatchProps,
  BalanceAccountSettingsStateProps,
} from 'components/features/pfm/BalanceAccountSettingsModule/BalanceAccountSettingsModule'
import { StoreDispatch } from 'interfaces/component'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { linkedMixedAccountsGuard, liveUpdateGuard2 } from 'guards/linkageGuards'
import { LinkedMixedOperation, ManualBalanceAccount, MixedBalanceAccount, ProviderBalanceAccount } from 'interfaces/linkage'
import { confirmMixedAccountOperationActionCreator, performMixedAccountOperationActionCreator, startLinkageSessionActionCreator } from 'actions/linkageActions'
import { createSelector } from 'reselect'
import { metaSelector } from 'selectors/globalSelectors'
import { isLiveUpdateStartedSelector, manualBalanceAccountsSelector, providerBalanceAccountsSelector, providersSelector } from 'selectors/linkageSelectors'
import { Category } from 'interfaces/meta'

const createMapStateToProps = () => createSelector(
  metaSelector,
  providerBalanceAccountsSelector,
  manualBalanceAccountsSelector,
  providersSelector,
  isLiveUpdateStartedSelector,
  isMobileSelector,
  (meta, providerBalanceAccounts, manualBalanceAccounts, providers, isLiveUpdateStarted, isMobile): BalanceAccountSettingsStateProps => {
    if (!providerBalanceAccounts || !manualBalanceAccounts || !providers) {
      throw createForgottenGuardError('BalanceAccountsSettingsModuleContainer', 'linkedMixedAccountsGuard')
    }

    const balanceAccounts = [
      ...getItemsFromCollection<ProviderBalanceAccount, 'cid'>(providerBalanceAccounts),
      ...getItemsFromCollection<ManualBalanceAccount, 'cid'>(manualBalanceAccounts),
    ]

    return {
      categories: concatCollections<Category, 'id'>(meta.asset_categories, meta.debt_categories),
      balanceAccounts,
      providers,
      isLiveUpdateStarted,
      isMobile,
    }
  }
)

const mapDispatchToProps: (dispatch: StoreDispatch) => BalanceAccountSettingsDispatchProps = (dispatch) => ({
  onPerformOperation: (account: MixedBalanceAccount, operation: LinkedMixedOperation) => {
    if (operation === 'update') {
      const cid = account.is_manual ? account.cid : account.providerAccountCid
      dispatch(startLinkageSessionActionCreator.request({ cid }))
      return
    }

    if (operation === 'delete' || operation === 'unlink') {
      dispatch(
        confirmMixedAccountOperationActionCreator(account, operation)
      )
      return
    }

    if (operation === 'link') {
      dispatch(
        performMixedAccountOperationActionCreator.request({ account, operation })
      )
      return
    }
  },
})

const providersGuards: Guards = [
  liveUpdateGuard2,
  linkedMixedAccountsGuard,
]

const BalanceAccountsSettingsModuleContainer = connectShadedLoadable<BalanceAccountSettingsStateProps>(
  createMapStateToProps, mapDispatchToProps, providersGuards
)(BalanceAccountSettingsModule)

export default BalanceAccountsSettingsModuleContainer
